<template>
  <div
    class="product-carousel critical-component-clear"
    :style="{
      '--section-spacing': spacing,
      '--section-spacing-l': spacingDesktop
    }"
  >
    <component
      :is="liquid.sectionIndex === 0 ? 'h1' : 'h2'"
      v-if="title"
      :id="`section-title-${liquid.sectionId}`"
      class="product-carousel__title text-heading-2xl text-heading-4xl-desktop"
    >
      <span v-text="title" />
    </component>

    <div
      v-if="!autoplayAllowed && controls && handles.length"
      class="product-carousel__controls"
    >
      <button
        class="product-carousel__button"
        type="button"
        :aria-label="$string('accessibility.carousel.previous')"
        @click="$refs.splide.go('<')"
      >
        <icon-chevron-left />
      </button>

      <div
        class="
          product-carousel__counter
          text-body-xs-label
          text-body-s-label-desktop
        "
      >
        <span
          class="product-carousel__count"
          v-text="counter.active"
        />

        <span v-text="counter.total" />
      </div>

      <button
        class="product-carousel__button"
        type="button"
        :aria-label="$string('accessibility.carousel.next')"
        @click="$refs.splide.go('>')"
      >
        <icon-chevron-right />
      </button>
    </div>

    <splide
      v-if="handles.length"
      ref="splide"
      :options="carouselOptions"
      @splide:mounted="handleSplideMounted"
      @splide:active="handleSplideActive"
    >
      <splide-slide
        v-for="(product) of products"
        :key="product.handle"
      >
        <product-card
          expand
          :loading-state="product === false"
          :product="product"
        />
      </splide-slide>
    </splide>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Carousel of product cards with optional title and controls.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 * @param {Array} handles - List of handles to load.
 * @param {String} title - Section heading.
 * @param {Boolean} controls - Enables the carousel controls.
 * @param {Boolean} autoplay - Automatically plays the carousel.
 * @param {String} spacing - Custom spacing value.
 * @param {String} spacingDesktop - Custom spacing value for desktop.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide } from '@splidejs/vue-splide'

import ProductCard from '~async/product-card/product-card'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import productQuery from '~/graphql/queries/product.gql'

import { formatProduct } from '~/helpers/format'
import { setIntersectionObserver } from '~/helpers/general'
import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductCarousel',

  components: {
    IconChevronLeft,
    IconChevronRight,
    ProductCard,
    Splide,
    SplideSlide,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        sectionIndex: 0,
      }),
    },
    handles: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    controls: {
      type: Boolean,
      default: false,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    spacing: {
      type: String,
      default: '',
    },
    spacingDesktop: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeSlide: 1,
      carouselOptions: {
        type: 'loop',
        gap: 'var(--spacing-2xs)',
        arrows: false,
        pagination: false,
        mediaQuery: 'min',
        perPage: 2,
        perMove: 1,
        pauseOnHover: false,
        pauseOnFocus: false,
        breakpoints: {
          [breakpoints.l]: {
            perPage: 4,
            padding: '20px',
          },
        },
      },
      isIntersecting: false,
      products: Array(this.handles.length).fill(false),
    }
  },

  computed: {

    /**
     * Map the Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the counter values.
     * @returns {Object} - The counter values.
     */
    counter() {
      return {
        active: String(this.activeSlide).padStart(2, 0),
        total: String(this.products.length).padStart(2, 0),
      }
    },

    /**
     * Defines if autoplay can be enabled.
     */
    autoplayAllowed() {
      return this.autoplay && this.isIntersecting && !this.isMobile
    },
  },

  watch: {

    /**
     * Resets the autoplay defaults when the allowed state changes.
     */
    autoplayAllowed() {
      this.setAutoplayDefaults()
    },
  },

  async mounted() {

    // Set products array to `false` while waiting to fetch product data.
    this.products = this.handles.map(() => false)
    await this.queryProducts()

    if (this.autoplay) {
      this.setAutoplayDefaults()
      this.setIntersectionEvents()
    }
  },

  methods: {

    /**
     * Sets the intersection event listeners.
     */
    setIntersectionEvents() {
      setIntersectionObserver({
        callback: (entries) => {
          entries.forEach(({ isIntersecting }) => {
            if (isIntersecting) {
              this.isIntersecting = true
            }
          })
        },
        element: this.$refs.splide.$el,
      })
    },

    /**
     * Queries products from the Storefront API.
     */
    async queryProducts() {
      try {
        const responses = await Promise.allSettled(
          this.handles.map((handle) => cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              handle,
              language: cnvs.store.language,
            },
          })),
        )

        if (this.$variable('settings.disableComponentReadyState')) {
          return
        }

        this.products = responses
          .map(({ value }) => formatProduct(value))
          .filter(Boolean)

      } catch (error) {
        this.products = []
        throw Error(error)
      }
    },

    /**
     * Sets the default state for the carousel autoplay.
     */
    setAutoplayDefaults() {
      if (!this.$refs.splide) {
        return
      }

      if (this.autoplayAllowed) {
        this.updateSplideSettings({
          interval: 0,
          speed: 100000,
        })

        this.$refs.splide.splide.Components.Autoplay.play()

        /**
         * Forces to the first slide to get around the `interval`.
         */
        this.$refs.splide.splide.go(1)
        return
      }

      this.updateSplideSettings({
        interval: 5000,
        speed: 400,
      })

      this.$refs.splide.splide.Components.Autoplay.pause()
    },

    /**
     * Updates the Splide settings and refreshes the carousel.
     * @param {Object} settings - Settings to update.
     */
    updateSplideSettings(settings) {
      if (!settings || !this.$refs.splide) {
        return
      }

      Object.keys(settings).forEach((key) => {
        this.$refs.splide.splide.options[key] = settings[key]
      })

      this.$refs.splide.splide.refresh()
    },

    /**
     * Handles the Splide mounted event.
     */
    handleSplideMounted() {
      this.$refs.splide.splide.refresh()
    },

    /**
     * Handles the Splide "active" event.
     * - Updates the local user-friendly active slide count.
     *
     * @param {Object} _ - The Splide instance.
     * @param {Object} payload - The event payload.
     * @param {Number} payload.slideIndex - The active slide index.
     */
    handleSplideActive(_, slideIndex) {
      this.activeSlide = slideIndex.isClone ? slideIndex.slideIndex + 1 : slideIndex.index + 1
    },
  },
}
</script>

<style lang="scss">
@import '@splidejs/vue-splide/css';
@import './product-carousel';
</style>
